// apps/pwa-pos/src/lib/consecutivo.ts
// Sprint 99 POS.5 — numeración local de ventas.
//
// El consecutivo vivía derivado del máximo presente en la cola de sync, así que
// `purgeSyncedQueue` lo reiniciaba a 1 y se emitían códigos duplicados. Ahora
// vive en su propia clave, monotónica, y se reserva ANTES de encolar: si el
// guardado de la venta falla se quema un número, que es inocuo, en vez de
// arriesgar un duplicado.

export const CONSECUTIVO_KEY = 'mibodega.pos.consecutivo';

export function leerConsecutivo(): number {
  try {
    if (typeof localStorage === 'undefined') return 0;
    const raw = localStorage.getItem(CONSECUTIVO_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

/**
 * Reserva y persiste el siguiente consecutivo.
 *
 * `pisoDesdeCola` cubre la migración de instalaciones que ya venían numerando
 * desde la cola y todavía no tienen la clave persistida.
 *
 * No atrapa el fallo de `setItem`: si no se puede persistir la numeración,
 * la venta no debe darse por cobrada.
 */
export function reservarConsecutivo(pisoDesdeCola = 0): number {
  const siguiente = Math.max(leerConsecutivo(), Math.floor(pisoDesdeCola)) + 1;
  localStorage.setItem(CONSECUTIVO_KEY, String(siguiente));
  return siguiente;
}

/**
 * Sube el piso del contador sin bajarlo nunca.
 *
 * La migración one-shot lo llama con el máximo entre la cola migrada y el mayor
 * sufijo de `historial.codigo`: `CONSECUTIVO_KEY` no existe en ningún equipo que
 * nunca corrió este build, y si la cola está vacía al actualizar el piso sería 0.
 *
 * No propaga el fallo de `setItem`: sembrar es una mejora del arranque, no una
 * venta. Si no se puede escribir, `reservarConsecutivo` seguirá derivando el
 * piso desde la cola como antes.
 */
export function sembrarConsecutivo(piso: number): number {
  const n = Number.isFinite(piso) ? Math.floor(piso) : 0;
  const actual = leerConsecutivo();
  if (n <= actual) return actual;
  try {
    localStorage.setItem(CONSECUTIVO_KEY, String(n));
  } catch {
    return actual;
  }
  return n;
}

export function formatearConsecutivo(numero: number, fechaIso: string): string {
  return `V-${fechaIso.slice(0, 4)}-${String(numero).padStart(6, '0')}`;
}
